import { useEffect, useRef, useState } from 'react';
import { APP, ROLE_LABELS_FA } from '@kia/shared';
import { useAuth } from '../auth/AuthProvider';
import { useFocusTrap } from '../lib/useFocusTrap';
import { Button } from '../ui';
import { ChangePasswordModal } from './ChangePasswordModal';
import { TwoFactorModal } from './TwoFactorModal';
import { NotificationBell } from '../messaging/NotificationBell';
import type { NotificationTarget } from '../messaging/notificationRouting';
import { GlobalSearch, type SearchType } from '../screens/GlobalSearch';
import { navFor, type View } from './navModel';

/**
 * The authenticated-shell navigation. One component, two shapes:
 *  • Desktop (≥960px): a persistent sidebar on the inline-start edge, collapsible to an icon rail
 *    (persisted in localStorage). 18 destinations are grouped into labelled sections so the eye
 *    lands fast — the old flat pill row didn't scale.
 *  • Mobile (<960px): a slim sticky top bar (menu + brand + bell) opens the same nav as an
 *    off-canvas drawer that behaves as a modal dialog — backdrop, focus trap, Esc, inert background,
 *    and focus returned to the trigger on close.
 * The routing (which screen a View maps to) stays in AppShell; this is pure chrome.
 */

const COLLAPSE_KEY = 'ct.nav.collapsed';

export function Sidebar({
  view,
  onNavigate,
  onJump,
  onNotify,
}: {
  view: View;
  onNavigate: (v: View) => void;
  onJump: (type: SearchType, id: number) => void;
  onNotify?: ((target: NotificationTarget) => void) | undefined;
}) {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false); // mobile drawer
  const asideRef = useRef<HTMLElement>(null);
  const wantSearchFocus = useRef(false);

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }

  // Mobile drawer = modal dialog: trap focus, mark the shell inert, Esc to close, restore focus.
  useFocusTrap(asideRef, open, {
    onEscape: () => setOpen(false),
    backgroundSelector: '.appmain',
  });

  // If the viewport grows past the mobile breakpoint while the drawer is open, close it — otherwise
  // the now-hidden drawer would leave `.appmain` inert and the page frozen with no visible way out.
  useEffect(() => {
    if (!open) return;
    const mq = window.matchMedia('(min-width: 960px)');
    const onChange = () => {
      if (mq.matches) setOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open]);

  // Ctrl/Cmd-K reaches search from any state. If the rail is collapsed (desktop) or the drawer is
  // closed (mobile), the search box — which owns the shortcut — isn't visible, so reveal it and mark
  // that we want to focus it. When the sidebar is already expanded, GlobalSearch's own handler
  // focuses it and we stay out of the way.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.key === 'k' || e.key === 'K') || !(e.metaKey || e.ctrlKey)) return;
      const mobile = window.matchMedia('(max-width: 959px)').matches;
      if (collapsed) {
        e.preventDefault();
        setCollapsed(false);
        wantSearchFocus.current = true;
      } else if (mobile && !open) {
        e.preventDefault();
        setOpen(true);
        wantSearchFocus.current = true;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed, open]);

  // Once the search has become visible (rail expanded / drawer opened), focus it.
  useEffect(() => {
    if (!wantSearchFocus.current) return;
    wantSearchFocus.current = false;
    const id = requestAnimationFrame(() =>
      asideRef.current?.querySelector<HTMLInputElement>('.nav-search input')?.focus(),
    );
    return () => cancelAnimationFrame(id);
  }, [collapsed, open]);

  if (!user) return null;
  const groups = navFor(user.role);
  // The visual icon-rail is a desktop-only state: inside the open mobile drawer we always show the
  // full nav (labels + search), regardless of the persisted collapse preference.
  const railCollapsed = collapsed && !open;
  const go = (v: View) => {
    onNavigate(v);
    setOpen(false);
  };

  const brand = (
    <div className="nav-brand">
      <img className="nav-mark" src="/assets/logo-mark.svg" alt={APP.company} />
      {!railCollapsed ? (
        <div className="nav-btxt">
          <b>{APP.name}</b>
          <span>{APP.company}</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <header className="nav-topbar">
        <button
          type="button"
          className="nav-hamb"
          aria-label="باز کردن منو"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          ☰
        </button>
        <img className="nav-mark" src="/assets/logo-mark.svg" alt={APP.company} />
        <b className="nav-topbar-title">{APP.name}</b>
        <span style={{ flex: 1 }} />
        <NotificationBell align="down" onNavigate={onNotify} />
      </header>

      {/* Mobile backdrop */}
      {open ? <div className="nav-backdrop" onClick={() => setOpen(false)} /> : null}

      {/* Sidebar — desktop static, mobile off-canvas modal drawer */}
      <aside
        ref={asideRef}
        className={`nav-side${railCollapsed ? ' collapsed' : ''}${open ? ' open' : ''}`}
        aria-label="ناوبری"
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
      >
        <div className="nav-side-head">
          {brand}
          <button
            type="button"
            className="nav-collapse"
            aria-label={collapsed ? 'باز کردن نوار کناری' : 'جمع کردن نوار کناری'}
            onClick={toggleCollapse}
          >
            {collapsed ? '»' : '«'}
          </button>
          <button
            type="button"
            className="nav-close"
            aria-label="بستن منو"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        {!railCollapsed ? (
          <div className="nav-search">
            <GlobalSearch
              onJump={(t, id) => {
                onJump(t, id);
                setOpen(false);
              }}
            />
          </div>
        ) : null}

        <nav className="nav-groups" aria-label="ناوبری اصلی">
          {groups.map((g) => (
            <div
              className="nav-group"
              key={g.id}
              role={g.label ? 'group' : undefined}
              aria-label={g.label || undefined}
            >
              {g.label && !railCollapsed ? <div className="nav-group-label">{g.label}</div> : null}
              {g.items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className={view === it.key ? 'nav-item active' : 'nav-item'}
                  aria-current={view === it.key ? 'page' : undefined}
                  aria-label={it.label}
                  title={railCollapsed ? it.label : undefined}
                  onClick={() => go(it.key)}
                >
                  <span className="nav-ico" aria-hidden>
                    {it.icon}
                  </span>
                  {!railCollapsed ? <span className="nav-label">{it.label}</span> : null}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="nav-foot">
          <div className="nav-footrow">
            <NotificationBell align="up" onNavigate={onNotify} />
            {!railCollapsed ? (
              <span className="nav-who">
                {user.name} <small>({ROLE_LABELS_FA[user.role]})</small>
              </span>
            ) : null}
          </div>
          {!railCollapsed ? (
            <>
              <Button variant="ghost" block onClick={() => setTwoFaOpen(true)}>
                تأیید دومرحله‌ای
              </Button>
              <Button variant="ghost" block onClick={() => setPwOpen(true)}>
                تغییر گذرواژه
              </Button>
            </>
          ) : null}
          <Button
            variant="secondary"
            block={!railCollapsed}
            onClick={logout}
            title={railCollapsed ? 'خروج' : undefined}
          >
            {railCollapsed ? '⎋' : 'خروج'}
          </Button>
        </div>
        {pwOpen ? <ChangePasswordModal onClose={() => setPwOpen(false)} /> : null}
        {twoFaOpen ? <TwoFactorModal onClose={() => setTwoFaOpen(false)} /> : null}
      </aside>
    </>
  );
}
